import * as React from "react";
import styled, { keyframes, useTheme } from "styled-components";
import { ColorName, getColor, AvatarSizeName, getAvatarSize } from "--components/theme";
import { CSSTransition } from 'react-transition-group';


const rotate = keyframes`
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
`;
const grow = (c: number) => keyframes`
    0% {
        stroke-dasharray: 0 ${c};
        stroke-dashoffset: 0;
    }
    50% {
        stroke-dasharray: ${c * 0.75} ${c * 0.25};
        stroke-dashoffset: -${c * 0.25};
    }
    100% {
        stroke-dasharray: 0.1 ${c - 0.1};
        stroke-dashoffset: -${c};
    }
`;

const Spinner = styled.circle<{ c: number, s?: ColorName | string }>`
    transform-origin: 50% 50%;
    animation: ${props => grow(props.c)} 2s ease-in-out infinite, ${rotate} 3s linear infinite;
    stroke: ${props => getColor(props.s, props.theme) || props.s || props.theme.palette.text.primary}; 
`;
type IndeterminateProgressProps = {
    color?: ColorName | string
    size?: AvatarSizeName | number
    style?: React.CSSProperties
    className?: string
}
const IndeterminateProgress: React.FC<IndeterminateProgressProps> = (props) => {
    const { color, size, ...svgProps } = props;

    const pxSize = typeof size === "number" ? size : getAvatarSize(size || "medium")
    const strokeWidth = pxSize / 8;
    const center = pxSize / 2;
    const radius = pxSize / 2 - strokeWidth / 2;
    const circumference = 2 * Math.PI * radius;

    return (
        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width={pxSize} height={pxSize} viewBox={`0 0 ${pxSize} ${pxSize}`} {...svgProps}>
            <Spinner
                c={circumference}
                s={color}
                cx={center}
                cy={center}
                r={radius}
                strokeWidth={strokeWidth}
                fill="none"
                strokeLinecap="round" />
        </svg>
    )
}

export const StyledCentredIndeterminateProgress = styled(IndeterminateProgress)`
    position: absolute;
    left:50%;
    top:50%;
    transform: translate(-50%,-50%);
`

export const DebouncedProgress: React.FC<{ showAfter: number }> = props => {
    const [show, setShow] = React.useState(false);
    React.useEffect(() => {
        const timer = setTimeout(() => setShow(true), props.showAfter)
        return () => clearTimeout(timer);
    }, []);
    if (show) return <>{props.children}</>
    return null;
}

const StyledIndeterMinateProgressWrapper = styled.div`
    position: relative;

    > svg:last-child {
        position: absolute;
        top: 50%; left: 50%;
        transform: translate(-50%, -50%);
        pointer-events: none;
        transition: opacity ${props => props.theme.transitions.long};
        
        opacity: 0;

        // Enter animations
        &.fade-enter {
            opacity: 0
        }
        &.fade-enter-active,
        &.fade-enter-done {
            opacity: 1;
        }

        // Exit animations
        &.fade-exit {
            opacity: 1;
        }
        &.fade-exit-active,
        &.fade-exit-done {
            opacity: 0;
        }
    }
`;
type IndeterminateProgressWrapperProps = IndeterminateProgressProps & {
    pending?: boolean
    delayMs?: number
}
const IndeterminateProgressWrapper: React.FC<IndeterminateProgressWrapperProps> = (props) => {
    const { pending, delayMs, children, size, color, style, className } = props;
    const theme = useTheme();
    const [visible, setVisible] = React.useState(false);
    const timeout = React.useRef<number>();

    React.useEffect(() => {
        if (pending && delayMs && !timeout.current) {
            timeout.current = window.setTimeout(() => {
                setVisible(pending);
                delete timeout.current;
            }, delayMs);
        } else {
            clearTimeout(timeout.current);
            setVisible(pending || false);
        }
        return () => clearTimeout(timeout.current);
    }, [pending, delayMs]);

    return <StyledIndeterMinateProgressWrapper style={style} className={className}>
        {children}
        <CSSTransition appear in={visible} timeout={parseInt(theme.transitions.standard, 10)} classNames="fade" unmountOnExit>
            <IndeterminateProgress size={size} color={color} />
        </CSSTransition>
    </StyledIndeterMinateProgressWrapper>
}

export default IndeterminateProgress;
export { IndeterminateProgress, IndeterminateProgressWrapper, IndeterminateProgressProps }; 